import { Box, xcss } from '@atlaskit/primitives';
import { type Database } from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import { NUMBERED_COLUMN_WIDTH } from '@databases/utils/constants';
import { viewHasCalculations } from '@databases/utils/database/utils';
import useView from '@databases/views/useView';
import React, { type FunctionComponent } from 'react';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import QuickCalculationsSelect from './QuickCalculationSelect';
import { getGridTemplateColumns } from './quickCalcUtils';

interface QuickCalculationsRowProps {
  columnWidths: (number | undefined)[];
  database: Database;
  containerWidth: number;
  canEditContent: boolean;
  shouldShowEntryPrefix: (view: DatabaseView) => boolean;
}

const getQuickCalculationSelectStyles = (width: number | undefined) => {
  return xcss({
    padding: 'space.0',
    width: width !== undefined ? `${width}px` : '',
  });
};

const QuickCalculationsRow: FunctionComponent<QuickCalculationsRowProps> = ({
  columnWidths,
  database,
  canEditContent,
  shouldShowEntryPrefix,
}) => {
  const { view } = useView();
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );

  // if the view has no calculations and the users can't edit the content, the
  // row would just be empty space. in this case, we should hide this empty space.
  if (!viewHasCalculations(view) && !canEditContent) {
    return null;
  }
  const showEntryPrefix = shouldShowEntryPrefix(view);

  const hasNumberedEntries =
    ((view.settings.layout.showNumberedEntries && database.entries.length !== 0) ||
      isVisualRefreshM1Enabled) &&
    showEntryPrefix;

  const width = hasNumberedEntries ? NUMBERED_COLUMN_WIDTH : 0;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: getGridTemplateColumns([width, ...columnWidths]),
      }}
    >
      {/* Takes space for numbered entries column */}
      <Box xcss={getQuickCalculationSelectStyles(width)} />
      {database.fields.map((fieldId: string) => {
        const widthIndex = database.fields.indexOf(fieldId);
        const isFirstField = database.fields.indexOf(fieldId) === 0;

        return (
          <Box
            key={fieldId}
            xcss={getQuickCalculationSelectStyles(columnWidths[widthIndex])}
          >
            <QuickCalculationsSelect
              fieldId={fieldId}
              database={database}
              width={columnWidths[widthIndex]}
              canEditContent={canEditContent}
              isFirstField={isFirstField}
            />
          </Box>
        );
      })}
    </div>
  );
};

export default React.memo(QuickCalculationsRow);
